import { HttpHeaders } from "@angular/common/http";
import { inject, Service } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { environment } from "../../core/environments/environment";
import { AdaaHelper } from "../../core/utils";
import { Adaa4RedirectService } from "../../pages/adaa4/services/adaa4-redirect-service";
import { Constants } from "../constants/constants";
import { PermissionAction, VerificationMethod } from "../constants/enums";
import {
  LinkAccount,
  LoginRequest,
  LoginResponse,
  MainResponseModel,
  NewPassword,
  SecurityQuestionAnswerModel,
  VerificationRequestEmail,
  VerificationRequestSms,
  VerificationRequestSubmit,
} from "../models";
import { ConsoleService, PermissionsService, SystemLayoutService } from ".";
import { MainService } from "./main-service";

@Service()
export class LoginService {
  private readonly _router = inject(Router);
  private readonly _toastrService = inject(ToastrService);
  private readonly _translateService = inject(TranslateService);
  private readonly _mainService = inject(MainService);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _consoleService = inject(ConsoleService);
  private readonly _systemLayoutService = inject(SystemLayoutService);
  private readonly _adaa4RedirectService = inject(Adaa4RedirectService);

  public login(data: LoginRequest) {
    return this._mainService.sendRequest({
      method: "POST",
      url: "authentication/login",
      data,
    });
  }

  public logout() {
    return this._mainService.sendRequest({
      method: "POST",
      url: "authentication/logout",
      data: {},
    });
  }

  public fetchVerificationCode(data: VerificationRequestEmail | VerificationRequestSms, type: VerificationMethod) {
    const url =
      type === VerificationMethod.EMAIL
        ? "authentication/sendVerificationCodeByEmail"
        : "authentication/sendVerificationCodeBySms";

    return this._mainService.sendRequest({
      method: "POST",
      url,
      data,
    });
  }

  public validateVerificationCode(data: VerificationRequestSubmit) {
    const url = "authentication/validatecode";

    return this._mainService.sendRequest({
      method: "POST",
      url,
      data,
    });
  }

  public resetPassword(data: LoginRequest) {
    const url = "authentication/forgotpassword";
    return this._mainService.sendRequest({
      method: "POST",
      url,
      data,
    });
  }

  public getUserbyToken(token: string) {
    const url = "users/getUserByToken";

    const httpHeader = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        token: token,
      }),
    };

    return this._mainService.sendRequest({
      method: "GET",
      url,
      headers: httpHeader.headers,
    });
  }

  public getSecurityQuestions(token: string) {
    const url = "userprofile/getSecurityQuestionsLogin";

    const httpHeader = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        token: token,
      }),
    };

    return this._mainService.sendRequest({
      method: "GET",
      url,
      headers: httpHeader.headers,
    });
  }

  public validateQuestions(token: string, data: SecurityQuestionAnswerModel) {
    const url = "userprofile/validateSecurityQuestions";
    const httpHeader = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        token: token,
      }),
    };

    return this._mainService.sendRequest({
      method: "POST",
      url,
      data,
      headers: httpHeader.headers,
    });
  }

  public validateCode(token: string, data: VerificationRequestSubmit) {
    const httpHeader = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        token: token,
      }),
    };

    const url = "authentication/validatecode";

    return this._mainService.sendRequest({
      method: "POST",
      url,
      data,
      headers: httpHeader.headers,
    });
  }

  public updatePassword(token: string, data: NewPassword) {
    const httpHeader = {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        token: token,
      }),
    };

    const url = "users/changepassword";

    return this._mainService.sendRequest({
      method: "POST",
      url,
      data,
      headers: httpHeader.headers,
    });
  }

  public uaePassLogin(token: string) {
    return this._mainService.sendRequest<MainResponseModel<LoginResponse>>({
      method: "POST",
      url: `authentication/uaepasslogin/${token}`,
      data: {},
    });
  }

  public uaePassLinkAccount(data: LinkAccount, uuid: string) {
    return this._mainService.sendRequest<MainResponseModel<LoginResponse>>({
      method: "POST",
      url: `authentication/manuallinking/${uuid}`,
      data,
    });
  }

  public uaePassLogout(timeOut: number = 10000) {
    setTimeout(() => {
      const url = `${environment.uaePass.logoutUrl}/?redirect_uri=${environment.baseUrl}`;
      window.location.href = url;
    }, timeOut);
  }

  public doLogin(hasSecurityQuestions: boolean, user: LoginResponse) {
    if (!user) {
      this._router.navigate(["/login"]);
      return;
    }

    const redirectUrl = AdaaHelper.getLocalStorage(Constants.localStorageKeys.redirectUrl) as string | undefined;
    AdaaHelper.deleteLocalStorage(Constants.localStorageKeys.redirectUrl);

    this._permissionsService.loadUserPermissions().subscribe({
      next: () => {
        this._navigateAfterLogin(hasSecurityQuestions, user, redirectUrl);
      },
    });
  }

  private _navigateAfterLogin(hasSecurityQuestions: boolean, user: LoginResponse, redirectUrl?: string) {
    if (this._mustCompleteProfile(hasSecurityQuestions, user, redirectUrl)) {
      this._toastrService.warning(this._translateService.instant("notification.warning.required_field_user_profile"));
      this._router.navigate(["/user-profile"]);
      return;
    }

    if (user.adaa4 && !redirectUrl) {
      this._router.navigate(["/homepage"]);
      return;
    }

    this._navigateByPermission(redirectUrl);
  }

  private _mustCompleteProfile(hasSecurityQuestions: boolean, user: LoginResponse, redirectUrl?: string): boolean {
    if (hasSecurityQuestions) return false;
    if (AdaaHelper.isDefinedAndNotEmpty(redirectUrl)) return false;
    if (user.adaa4 && this._hasAnyAdaa4DashboardPermission()) return false;

    return true;
  }

  private _hasAnyAdaa4DashboardPermission(): boolean {
    return [
      Constants.CONSTANT_PERMISSIONS.GOVERNMENT_PERFORMANCE_DASHBOARD,
      Constants.CONSTANT_PERMISSIONS.STRATEGY_DASHBOARD,
      Constants.CONSTANT_PERMISSIONS.INTERNATIONAL_DASHBOARD,
      Constants.CONSTANT_PERMISSIONS.AGENTIC_DASHBOARD,
    ].some((objectTypeId) => this._hasDashboardPermission(objectTypeId));
  }

  private _hasDashboardPermission(objectTypeId: number): boolean {
    return this._permissionsService.hasPermission([
      {
        objectTypeId,
        permissionAction: PermissionAction.view,
      },
    ]);
  }

  private _navigateByPermission(redirectUrl?: string) {
    const adaa4Dashboards = [
      environment.adaa4Dashboards.govDashboardAE,
      environment.adaa4Dashboards.govDashboardEN,
      environment.adaa4Dashboards.strategyDashboardAE,
      environment.adaa4Dashboards.strategyDashboardEN,
      environment.adaa4Dashboards.internationalDashboardAE,
      environment.adaa4Dashboards.internationalDashboardEN,
      environment.adaa4Dashboards.agenticDashboardAE,
      environment.adaa4Dashboards.agenticDashboardEN,
    ];
    if (redirectUrl) {
      if (adaa4Dashboards.includes(redirectUrl)) return this._navigateToExternalUrl(redirectUrl);

      this._systemLayoutService.init();

      this._router.navigateByUrl(redirectUrl);
      return;
    }

    const hasTpDashboardPermission = this._permissionsService.hasPermission([
      {
        objectTypeId: Constants.CONSTANT_PERMISSIONS.NATIONAL_PROJECTS_DASHBOARD,
        permissionAction: PermissionAction.view,
      },
    ]);

    const hasHomepagePermission = this._permissionsService.hasPermission(
      [
        {
          objectTypeId: Constants.CONSTANT_PERMISSIONS.HOMEPAGE,
          permissionAction: PermissionAction.view,
        },
      ],
      "and"
    );

    if (hasHomepagePermission) {
      this._router.navigate(["/dashboard/target"]);
    } else if (hasTpDashboardPermission) {
      this._router.navigate(["/national-projects-dashboard"]);
    } else {
      this._router.navigate(["/console"]);
    }
    return;
  }

  private _navigateToExternalUrl(redirectUrl: string) {
    if (this._hasAdaa4Permission(redirectUrl)) {
      this._adaa4RedirectService.open(redirectUrl);
      return;
    } else {
      return this._router.navigate(["/console"]);
    }
  }

  private _hasAdaa4Permission(redirectUrl: string): boolean {
    const dashboards = environment.adaa4Dashboards;

    const permissionByDashboardUrl: Record<string, number> = {
      [dashboards.govDashboardAE]: Constants.CONSTANT_PERMISSIONS.GOVERNMENT_PERFORMANCE_DASHBOARD,
      [dashboards.govDashboardEN]: Constants.CONSTANT_PERMISSIONS.GOVERNMENT_PERFORMANCE_DASHBOARD,
      [dashboards.strategyDashboardAE]: Constants.CONSTANT_PERMISSIONS.STRATEGY_DASHBOARD,
      [dashboards.strategyDashboardEN]: Constants.CONSTANT_PERMISSIONS.STRATEGY_DASHBOARD,
      [dashboards.internationalDashboardAE]: Constants.CONSTANT_PERMISSIONS.INTERNATIONAL_DASHBOARD,
      [dashboards.internationalDashboardEN]: Constants.CONSTANT_PERMISSIONS.INTERNATIONAL_DASHBOARD,
      [dashboards.agenticDashboardAE]: Constants.CONSTANT_PERMISSIONS.AGENTIC_DASHBOARD,
      [dashboards.agenticDashboardEN]: Constants.CONSTANT_PERMISSIONS.AGENTIC_DASHBOARD,
    };

    const requiredPermission = permissionByDashboardUrl[redirectUrl];
    if (!requiredPermission) return false;

    return this._hasDashboardPermission(requiredPermission);
  }
}
